/**
 * ClockInGate
 * -----------
 * After login, office staff who are NOT already on the clock get a popup
 * prompting them to clock in for the day. It reuses the real office punch
 * clock (`/api/admin-time/clock` + `/api/admin-time/punch`), so:
 *   - PCW / field roles (403 on the clock endpoint) never see it.
 *   - Anyone already clocked in (or who already clocked in today) is skipped.
 * Shows once per browser session per user; a fresh login re-prompts if the
 * user still isn't on the clock. Sits BELOW the HIPAA WelcomeGate (z-index)
 * so it appears once that's acknowledged.
 *
 * Presentation: a NON-BLOCKING prompt docked bottom-right (not a full-screen
 * modal) so it never covers page content, but it has NO dismiss — it persists
 * until the user clocks in (mandatory nag, per Dawn).
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Clock, LogIn, Loader2, Minus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#032866";
const GOLD = "#FFDA00";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const longDate = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });

// Specific users who should NEVER get the clock-in prompt (business request).
// Matched by name/email so it works in production even though the account
// isn't in the preview DB. Waleska ("W") Martinez is excluded per Dawn.
const norm = (s) => (s || "").trim().toLowerCase();
const isExcludedFromClockIn = (u) => {
  if (!u) return false;
  const f = norm(u.first_name);
  const l = norm(u.last_name);
  const em = norm(u.email);
  if (em.includes("waleska")) return true;
  if (l.startsWith("martinez") && (f.startsWith("waleska") || f === "w")) return true;
  return false;
};

export default function ClockInGate() {
  const { user, isAuthenticated } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user || isExcludedFromClockIn(user)) return;

    let cancelled = false;
    const checkClock = async () => {
      try {
        const res = await fetch(`${API}/api/admin-time/clock`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (cancelled) return;
        // 403 => not an office/time-clock role (PCW etc.) → never prompt.
        if (!res.ok) return;
        const d = await res.json().catch(() => ({}));
        // Nag whenever they are NOT currently on the clock — a clocked-out
        // user who already worked earlier still gets re-prompted on each
        // login/reload (per Dawn: "whenever they aren't clocked in, nag").
        // When they ARE on the clock (incl. after punching in via the Time
        // Card widget) hide the nag.
        const onTheClock = d.state === "clocked_in" || d.state === "on_lunch";
        setShow(!onTheClock);
      } catch {
        // Network hiccup — fail quiet, don't block the app.
      }
    };
    checkClock();
    // Re-check whenever any clock-aware widget punches in/out so this nag
    // stays in sync with the dashboard Time Card.
    const onClockChanged = () => checkClock();
    window.addEventListener("avida:clock-changed", onClockChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("avida:clock-changed", onClockChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const close = () => setShow(false);

  const clockIn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin-time/punch`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ kind: "in" }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail?.message || d?.detail || `HTTP ${res.status}`);
      toast.success("You're clocked in — have a great shift!");
      close();
      // Nudge any live time widgets to refresh.
      window.dispatchEvent(new CustomEvent("avida:clock-changed"));
    } catch (e) {
      toast.error(e.message || "Couldn't clock you in — try the Time Card.");
    }
    setBusy(false);
  };

  if (!isAuthenticated || !user || !show) return null;

  const firstName = user.first_name || (user.email || "").split("@")[0] || "there";

  // Collapsed state: a small pill that stays visible (still nagging, still no
  // dismiss) but barely covers the screen so it never blocks page controls.
  if (collapsed) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[9990] pointer-events-none"
        role="dialog"
        aria-label="Clock in to start your day"
        data-testid="clockin-gate"
      >
        <button
          onClick={() => setCollapsed(false)}
          className="pointer-events-auto flex items-center gap-2 h-10 pl-3 pr-4 rounded-full shadow-xl ring-1 ring-black/5 hover:brightness-110 transition-all animate-in fade-in duration-200"
          style={{ background: NAVY, color: GOLD }}
          data-testid="clockin-gate-expand-btn"
          aria-label="Expand clock-in prompt"
        >
          <Clock className="w-4 h-4" strokeWidth={2.2} />
          <span className="text-xs font-black">Clock in</span>
        </button>
      </div>
    );
  }

  // Non-blocking, mandatory prompt: docked bottom-right so it never covers the
  // page content, but it has NO full dismiss — it can only be collapsed to the
  // pill above, and persists on every login/reload until the user actually
  // clocks in (per Dawn's "keep nagging" rule).
  return (
    <div
      className="fixed bottom-4 right-4 z-[9990] w-[calc(100%-2rem)] max-w-sm pointer-events-none"
      role="dialog"
      aria-label="Clock in to start your day"
      data-testid="clockin-gate"
    >
      <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white pointer-events-auto animate-in slide-in-from-bottom-4 fade-in duration-300 ring-1 ring-black/5">
        <div
          className="absolute top-0 left-0 right-0 h-1.5 z-10"
          style={{ background: "linear-gradient(90deg, #FFDA00 0%, #0866FF 55%, #0044A8 100%)" }}
          aria-hidden="true"
        />
        <button
          onClick={() => setCollapsed(true)}
          className="absolute top-2.5 right-2.5 z-20 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          data-testid="clockin-gate-collapse-btn"
          aria-label="Minimize clock-in prompt"
          title="Minimize"
        >
          <Minus className="w-4 h-4" strokeWidth={2.5} />
        </button>
        <div className="px-5 pt-6 pb-5">
          <div className="flex items-start gap-3.5">
            <div
              className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: NAVY }}
            >
              <Clock className="w-6 h-6" style={{ color: GOLD }} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-black tracking-tight leading-tight" style={{ color: NAVY }}>
                {greeting()}, {firstName}
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                {longDate()}
              </p>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">
                You're not on the clock right now. Clock in to start tracking your time for today.
              </p>
            </div>
          </div>

          <button
            onClick={clockIn}
            disabled={busy}
            className="mt-4 w-full h-11 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-60"
            style={{ background: NAVY, color: GOLD }}
            data-testid="clockin-gate-clock-in-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {busy ? "Clocking you in…" : "Clock in now"}
          </button>
        </div>
      </div>
    </div>
  );
}
